type IndicatorGroup = {
    indicators: Map<string, any[]>
    colors: Map<string, string>
    title?: string
}

type IndicatorsState = State & {
    start: { info: { span: { normalized: string } } }
    span: { begin: number; end: number }
    groups: IndicatorGroup[]
    markers: Array<{ timestamp: number; title: string }>
    // Per-group title/colors sent once by the query as @meta (title, __color, title_2, __color_2, ...),
    // so per-event rows don't have to repeat the whole color lookup.
    metaGroups: Array<{ title?: string; colors?: Map<string, string> }>
}

function emptyState(): IndicatorsState {
    return {
        start: { info: { span: { normalized: '' } } },
        span: { begin: 0, end: 0 },
        groups: [],
        markers: [],
        metaGroups: []
    }
}

function metaGroupsFromInfo(info: Record<string, any> = {}): IndicatorsState['metaGroups'] {
    const metaGroups: IndicatorsState['metaGroups'] = []
    for (const key of Object.keys(info)) {
        const match = /^(title|__color)(?:_(\d+))?$/.exec(key)
        if (!match) continue

        const index = match[2] ? Number(match[2]) - 1 : 0
        const meta = (metaGroups[index] = metaGroups[index] ?? {})
        if (match[1] === 'title') meta.title = info[key]
        else if (Array.isArray(info[key])) meta.colors = arrayToColorMap(info[key])
    }

    return metaGroups
}

const ignore = ['positive_fit', 'zero_fit']

function updateIndicators(
    map: Map<string, any[]>,
    values: Record<string, string>,
    timestamp: number
) {
    for (const [key, rawValue] of Object.entries(values)) {
        if (ignore.includes(key)) continue

        const duration = Number(rawValue)
        if (!isNaN(duration) && duration > 0) {
            const series = map.get(key) ?? []
            const startTs = timestamp
            const endTs = timestamp + duration * 1000
            series.push([startTs, true])
            series.push([endTs, false])
            map.set(key, series)
        }
    }
}

function arrayToColorMap(arr: { key: string; value: string }[] = []): Map<string, string> {
    const map = new Map<string, string>()
    for (const { key, value } of arr) map.set(key, value)

    return map
}

export default function rigIndicatorsReducer(state: IndicatorsState = emptyState(), event: Event): IndicatorsState {
    switch (event?.type) {
        case 'start': {
            const started = emptyState()
            started.metaGroups = metaGroupsFromInfo(event?.events?.info)

            return started
        }

        case 'event':
            for (const it of event.events) {
                const { timestamp, marker_timestamp, marker_title } = it

                if (marker_timestamp && marker_title) {
                    state.markers.push({ timestamp: marker_timestamp, title: marker_title })
                }

                // Dynamically detect all indicator groups in the event
                const indicatorKeys = Object.keys(it).filter(k => k.startsWith('indicators'))
                for (const key of indicatorKeys) {
                    const index = key === 'indicators' ? 0 : Number(key.split('_')[1]) - 1
                    const colorKey = `__color${index === 0 ? '' : `_${index + 1}`}`
                    const titleKey = `title${index === 0 ? '' : `_${index + 1}`}`

                    if (!state.groups[index]) {
                        const meta = state.metaGroups[index]
                        state.groups[index] = {
                            indicators: new Map(),
                            colors: new Map(meta?.colors ?? []),
                            title: meta?.title ?? ''
                        }
                    }

                    const group = state.groups[index]
                    const values = it[key]
                    const colors = it[colorKey]
                    const title = it[titleKey]

                    if (Array.isArray(colors)) {
                        const colorMap = arrayToColorMap(colors)
                        for (const [k, rawValue] of Object.entries(values)) {
                            const duration = Number(rawValue)
                            if (!isNaN(duration) && duration > 0 && colorMap.has(k)) {
                                group.colors.set(k, colorMap.get(k)!)
                            }
                        }
                    }

                    updateIndicators(group.indicators, values, timestamp)
                    if (title) group.title = title
                }
            }

            return state

        case 'span':
            state.span = event.events

            return state

        default:
            return state
    }
}
